import React, { useCallback, useEffect, useRef, useState } from "react";
import { CarFront, CheckCircle2, Wallet } from "lucide-react";
import { supabase } from "../services/supabaseClient";
import { trackBookingFunnelEvent } from "../utils/bookingFunnelTracking";
import { rapidSearchWindowState } from "../utils/rapidSearchWindowState.mjs";
import { rideNowSearchProgress, rideNowActivityMessage } from "../utils/rideNowSearchProgress.mjs";

const safeCheckoutUrl = (value) => {
  try {
    const url = new URL(String(value || ""));
    return url.protocol === "https:" && url.hostname === "checkout.paystack.com"
      ? url.href : null;
  } catch {
    return null;
  }
};

/** Restricted Ride Now passenger payment authorization and dispatch status. */
export default function RapidRideNowPayment({ requestId, marketId = null, journeySessionId = null, onFinished, onLeave, onRetry, onScheduleInstead, autoVerifyOnReturn = false, autoBegin = false }) {
  const [stage, setStage] = useState("checking");
  const observedMilestones = useRef(new Set());
  const reportMilestone = (name, reasonCode = null) => {
    if (!requestId) return;
    const key = `${requestId}:${name}:${reasonCode || ''}`;
    if (observedMilestones.current.has(key)) return;
    observedMilestones.current.add(key);
    void trackBookingFunnelEvent(name, {
      reasonCode,
      rideMode: 'ride_now',
      pageSource: 'ride_now_checkout',
      marketId,
      sessionId: journeySessionId,
    });
  };
  const [savedCards, setSavedCards] = useState([]);
  const [savedCardsLoaded, setSavedCardsLoaded] = useState(false);
  const [saveNewCard,setSaveNewCard] = useState(false);
  const [fare,setFare] = useState(null);
  const [companyFunded,setCompanyFunded] = useState(false);
  const [paymentHoldStatus,setPaymentHoldStatus] = useState('not_started');
  const [promoInput,setPromoInput] = useState("");
  const [promoBusy,setPromoBusy] = useState(false);
  const [promoError,setPromoError] = useState("");
  const [promoNote,setPromoNote] = useState("");
  const [selectedCard, setSelectedCard] = useState("");
  const [paymentMethod,setPaymentMethod]=useState("card");
  const [walletBalance,setWalletBalance]=useState(null);
  const [walletStatus,setWalletStatus]=useState("not_started");
  const [walletUsed,setWalletUsed]=useState(false);
  useEffect(() => {
    let mounted = true;
    void supabase.rpc("uniair_rapid_my_saved_cards_v1")
      .then(({ data, error }) => {
        if (mounted && !error && Array.isArray(data)) {
          setSavedCards(data);
          setSelectedCard(old => old || data[0]?.saved_card_id || "");
        }
        if (mounted) setSavedCardsLoaded(true);
      });
    return () => { mounted = false; };
  }, []);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [confirmedTripId, setConfirmedTripId] = useState(null);
  const [searchExpiresAt, setSearchExpiresAt] = useState(null);
  const [searchReserved, setSearchReserved] = useState(false);
  const [searchStartedAt,setSearchStartedAt] = useState(null);
  const [driversContacted,setDriversContacted] = useState(0);
  const [activeDriverOffers,setActiveDriverOffers] = useState(0);
  const previousOfferStatus = useRef({active:0,reserved:false});
  const [recentDeclineUntil,setRecentDeclineUntil] = useState(0);
  const [safeToSchedule,setSafeToSchedule] = useState(false);
  const demandRegistered = useRef(null);
  const supplyDeadlineRef = useRef(null);
  const supplyAutoResumeAt = useRef(0);
  const supplyPollingInFlightRef = useRef(false);
  const supplyPreparingRef = useRef(false);
  const [connectingToDriver, setConnectingToDriver] = useState(false);
  // The no-supply check starts the SAME five-minute passenger search UI.
  // No funds are reserved until an eligible online driver is detected.

  // Record terminal and intermediate stages only when the verified status
  // reaches them. Polling must not inflate funnel counts.
  useEffect(() => {
    if (fare && Number.isFinite(fare.total)) reportMilestone('fare_shown');
  }, [fare?.total, requestId]);
  useEffect(() => {
    if (searchStartedAt) reportMilestone('driver_search_started');
    if (driversContacted > 0) reportMilestone('driver_offers_found');
  }, [searchStartedAt, driversContacted, requestId]);
  useEffect(() => {
    if (stage === 'confirmed') reportMilestone('driver_confirmed');
    if (stage === 'supply_timeout') reportMilestone('no_availability_found', 'NO_DRIVER_AVAILABLE');
    if (stage === 'payment_setup_unavailable') reportMilestone('payment_failed', 'PAYMENT_SETUP_UNAVAILABLE');
    if (stage === 'verification_unavailable') reportMilestone('payment_verification_pending', 'PROVIDER_VERIFICATION_UNAVAILABLE');
    if (stage === 'expired') reportMilestone('request_expired', 'REQUEST_EXPIRED');
  }, [stage, requestId]);
  const [now, setNow] = useState(Date.now());
  // Keep an unpaid demand search on the same visual countdown while the
  // payment provider and dispatch ledger remain untouched.
  const noSupplyKey = `uniair:rapid:no-supply:${requestId}`;
  const noSupplyLatch = useRef(false);
  const markNoSupply = () => {
    noSupplyLatch.current = true;
    setSearchReserved(false);
    if (!supplyDeadlineRef.current) {
      supplyDeadlineRef.current = new Date(Date.now() + 300000).toISOString();
    }
    setSearchExpiresAt(supplyDeadlineRef.current);
    setSafeToSchedule(false);
    try { window.sessionStorage.setItem(noSupplyKey, '1'); } catch { /* private mode */ }
  };
  const clearNoSupply = () => {
    noSupplyLatch.current = false;
    try { window.sessionStorage.removeItem(noSupplyKey); } catch { /* private mode */ }
  };
  useEffect(() => {
    try { noSupplyLatch.current = window.sessionStorage.getItem(noSupplyKey)==='1'; }
    catch { noSupplyLatch.current=false; }
  }, [noSupplyKey]);
  const statusChecking = useRef(false);
  const latestStage = useRef(stage);
  latestStage.current = stage;
  const expirationInFlight = useRef(false);
  const lastExpirationAttemptAt = useRef(0);
  const returnVerificationStarted = useRef(false);
  const autoPrepareInitiated = useRef(false);

  const checkTrip = useCallback(async () => {
    if (statusChecking.current) return;
    statusChecking.current = true;
    // Do not allow a stalled mobile request to block every later status poll.
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 9000);
    let status, statusError;
    try {
      const result = await supabase.rpc(
        "uniair_rapid_my_request_status_v1", { p_request_id: requestId },
      ).abortSignal(controller.signal);
      status = result.data;
      statusError = result.error;
    } catch (error) {
      statusError = error;
    } finally {
      window.clearTimeout(timeout);
      statusChecking.current = false;
    }
    if (!statusError && status?.ok) {
      setFare({
        original:Number(status.original_fare||0),
        total:Number(status.final_fare||0),
        discount:Number(status.promo_discount||0),
        code:status.promo_code||null,
        pickup:status.pickup_location||'',
        destination:status.dropoff_location||'',
        passengers:Number(status.passenger_count||1),
      });
      setCompanyFunded(Boolean(status.company_funded || Number(status.final_fare)===0));
      setPaymentHoldStatus(status.hold_status || 'not_started');
      setWalletUsed(Boolean(status.wallet_used));
      setWalletStatus(status.wallet_status||'not_started');
      // The balance is status-owned; never hide Wallet because an older
      // status response omits the field or a profile lookup is unavailable.
      setWalletBalance(status.wallet_balance == null ||
        !Number.isFinite(Number(status.wallet_balance))
          ? null : Number(status.wallet_balance));
      // A sponsored reservation is not available for another ride until
      // its funding reservation was released or cancelled. Likewise, a
      // Paystack authorization needs actual provider release evidence.
      const sponsoredClear = status.company_funded &&
        ['not_started','released','cancelled','expired','failed'].includes(status.company_funding_status);
      const cardClear = !status.wallet_used && (status.provider_released ||
        (!status.company_funded &&
          ['created','not_started','failed','expired'].includes(status.hold_status)));
      const walletClear = status.wallet_used && status.wallet_status==='released';
      setSafeToSchedule(Boolean(sponsoredClear || cardClear || walletClear));
      if(status.search_started_at) setSearchStartedAt(status.search_started_at);
      setDriversContacted(Math.max(0,Number(status.drivers_contacted||0)));
      const activeOffersNow = Math.max(0, Number(status.active_driver_offers || 0));
      const reservedNow = status.search_status === "driver_reserved";
      if ((previousOfferStatus.current.active > 0 || previousOfferStatus.current.reserved) &&
          !activeOffersNow && !reservedNow && status.search_status === "searching")
        setRecentDeclineUntil(Date.now() + 8000);
      if (activeOffersNow || reservedNow) setRecentDeclineUntil(0);
      previousOfferStatus.current = {active:activeOffersNow,reserved:reservedNow};
      setActiveDriverOffers(activeOffersNow);
      if (status.confirmed && status.private_trip_id) {
        reportMilestone('driver_confirmed');
        setConfirmedTripId(status.private_trip_id);
        setStage("confirmed");
        setMessage(status.company_funded
          ? "Driver confirmed. UniAir is covering your fare. No card is charged."
          : status.wallet_used
            ? "Driver confirmed. Your wallet fare is reserved until trip completion."
            : "Driver confirmed. Your card will be captured after trip completion.");
        return;
      }
      // A rejected Paystack setup may leave a local hold uncertain. Show the
      // durable blocker even if the 15-minute Ride Now request has expired.
      // No payment restart is permitted until provider reconciliation.
      if (status.payment_setup_unavailable === true &&
          status.hold_status === "uncertain") {
        setStage("payment_setup_unavailable");
        setCheckoutUrl(null);
        setMessage("We couldn't open Paystack's secure checkout for this request. Your ride is not confirmed. UniAir still needs to check whether Paystack created a card hold. Please do not retry payment until support resolves it.");
        return;
      }
      // An expired/cancelled request is not payable, even if its old browser
      // session still contains its UUID. A new request is safe only when no
      // provider authorization is pending or active.
      // Provider-confirmed release remains definitive even if the request
      // expired while Paystack was releasing the hold.
      if (status.wallet_used && status.wallet_status==='released') {
        setSearchReserved(false);
        setStage('released');
        setCheckoutUrl(null);
        setMessage('Your unused Ride Now fare has been returned to UniAir Wallet.');
        return;
      }
      if (status.provider_released) {
        setSearchReserved(false);
        setStage("released");
        setCheckoutUrl(null);
        setMessage("Paystack reports that this unused card hold has been released. No trip was charged.");
        return;
      }
      if (status.needs_hold_release || ["no_driver", "cancelled"].includes(status.search_status)) {
        reportMilestone(status.search_status === 'cancelled'
          ? 'driver_search_cancelled' : 'driver_search_expired',
          status.search_status === 'cancelled' ? 'SEARCH_CANCELLED' : 'NO_DRIVER_AVAILABLE');
        setStage("no_driver");
        setSearchReserved(false);
        setCheckoutUrl(null);
        setMessage(status.company_funded
          ? "No driver was found. Your fully funded promo remains unused and no card was charged."
          : status.wallet_used
          ? (status.wallet_status==='released'
            ? 'No driver was found. The fare has been returned to UniAir Wallet.'
            : 'Driver search ended. Your reserved wallet fare is being reconciled; do not reserve again.')
          : status.search_status === "cancelled"
          ? "Search cancelled. Your unused card authorization is being released."
          : "No driver was found. UniAir is releasing any authorized hold. Release is confirmed by Paystack.");
        return;
      }
      const requestInactive = status.request_status !== "requested" ||
        (status.request_expires_at &&
          Date.parse(status.request_expires_at) <= Date.now());
      if (requestInactive) {
        setCheckoutUrl(null);
        if (status.wallet_used && status.wallet_status!=='released') {
          setStage('needs_verification');
          setMessage('This request ended. Your wallet refund is being checked. Do not reserve another fare yet.');
          return;
        }
        if (["created", "not_started", "failed", "expired"].includes(status.hold_status)) {
          setStage("expired");
          setMessage("This Ride Now request has expired or closed without an authorized card hold. Start a fresh request to check the current fare and driver availability.");
        } else {
          setStage("needs_verification");
          setMessage("This request is no longer open, but its card authorization still needs reconciliation. Do not start another payment until UniAir confirms the hold has been released or settled.");
        }
        return;
      }
      if (status.search_status === "searching" ||
          status.search_status === "driver_reserved") {
        // The original deadline applies even to an accepted but unconfirmed offer.
        if (rapidSearchWindowState({
          status: status.search_status,
          expiresAt: status.search_expires_at,
        }) === "search_finalizing") {
          setStage("search_finalizing");
          setSearchReserved(false);
          setSearchExpiresAt(status.search_expires_at);
          setCheckoutUrl(null);
          return;
        }
        reportMilestone('payment_authorized');
        reportMilestone('driver_search_started');
        if (Number(status.drivers_contacted || 0) > 0) reportMilestone('driver_offers_found');
        setStage("authorized");
        setSearchReserved(status.search_status === "driver_reserved");
        setSearchExpiresAt(status.search_expires_at || null);
        setCheckoutUrl(null);
        setMessage("");
        return;
      }
      if (status.wallet_status==='reserved') {
        setStage('wallet_search_pending');
        setMessage('Your fare is reserved in UniAir Wallet. Driver search can be retried without another deduction.');
        return;
      }
      if (status.hold_status === "pending_customer") {
        // Preserve the provider authentication error until the server obtains
        // independent evidence of authorization or a released hold.
        setStage(previous => previous === "verification_unavailable"
          ? previous : "pending_customer");
        setMessage(previous => previous.includes("Paystack verification is currently unavailable")
          ? previous
          : autoVerifyOnReturn
            ? "Confirming your Paystack authorization automatically…"
            : "Finish your existing secure checkout to continue.");
        return;
      }
      if (["initializing", "verifying", "uncertain", "capture_pending",
          "release_pending"].includes(status.hold_status)) {
        setStage("needs_verification");
        setCheckoutUrl(null);
        setMessage("UniAir cannot yet confirm the earlier authorization. Do not start another card hold while verification is pending.");
        return;
      }
      if (["failed", "expired"].includes(status.hold_status)) {
        setStage("needs_verification");
        setCheckoutUrl(null);
        setMessage("The authorization was not confirmed. Check its status before attempting another reservation.");
        return;
      }
      if (status.hold_status === "created" || status.hold_status === "not_started") {
        // The request remains payable, but a previous no-driver check is
        // authoritative until this passenger chooses Retry. An unrelated
        // polling response must never bounce back to Paystack.
        if (noSupplyLatch.current &&
            ['not_started','no_driver','no_candidates'].includes(status.search_status)) {
          const elapsed = supplyDeadlineRef.current &&
            Date.parse(supplyDeadlineRef.current) <= Date.now();
          setStage(elapsed ? 'supply_timeout' : 'supply_search');
          setCheckoutUrl(null);
          setSafeToSchedule(Boolean(elapsed));
          return;
        }
        // The server confirms no initialization began for this request.
        // Clear any stale browser-side verification error.
        setStage("idle");
        setCheckoutUrl(null);
        setMessage("");
        return;
      }
    } else {
      // A temporary network failure must not erase an already active search
      // or the original five-minute deadline. Only the initial checkout
      // should fail closed when status cannot be verified.
      if (["authorized","supply_search","search_finalizing"].includes(latestStage.current)) {
        return;
      }
      setStage("status_error");
      setMessage("UniAir could not verify this Ride Now request. Check the request status before authorizing any card.");
      return;
    }
    // Existing trip list is a fallback for delayed RPC deployments.
    const { data, error } = await supabase.rpc("get_my_private_ride_upcoming_trips_v2");
    if (error || !Array.isArray(data)) return;
    const trip = data.find(item => String(item.request_id) === String(requestId) &&
      ["confirmed", "in_progress"].includes(String(item.status)));
    if (trip) {
      setConfirmedTripId(trip.private_trip_id);
      setStage("confirmed");
      setMessage(walletUsed
        ? "Driver confirmed. Your wallet fare is reserved until completion."
        : "Driver confirmed. Your card will be captured after trip completion.");
    }
  }, [requestId, walletUsed]);

  useEffect(() => {
    void checkTrip();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void checkTrip();
    }, 3000);
    const onFocus = () => { if (document.visibilityState === "visible") void checkTrip(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [checkTrip]);

  useEffect(() => {
    if (!["authorized","supply_search","search_finalizing"].includes(stage) &&
        !(stage === "checking" && connectingToDriver && searchExpiresAt)) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [stage, connectingToDriver, searchExpiresAt]);

  async function applyPromo(code=promoInput) {
    if(promoBusy || busy || stage!=='idle')return;
    setPromoBusy(true);setPromoError('');setPromoNote('');
    try{
      const {data,error}=await supabase.rpc('uniair_rapid_set_my_promo_v1',{
        p_request_id:requestId,p_code:code.trim()||null,p_apply:true,
      });
      if(error || !data?.ok)throw new Error(
        error?.message||data?.code||'That promo could not be applied.');
      const amount=Number(data.discount_amount||0).toFixed(2);
      setPromoNote(data.discount_code
        ? data.partially_capped
          ? `Promo applied with the code's admin-defined limit · R${amount} off`
          : `Promo applied · R${amount} off`
        : 'Promo removed');
      await checkTrip();
    }catch(error){
      const code=String(error?.message||'');
      const description=code.includes('PROMO_BELOW_PAYSTACK_MINIMUM') ||
        code.includes('DISCOUNTED_FARE_BELOW_PAYSTACK_MINIMUM')
        ? 'This promo leaves less than R5 to pay. Paystack cannot authorize that amount. Try another promo, or ask UniAir about a fully sponsored R0 ride.'
        : code.includes('PROMO_REQUIRES_ADMIN_REVIEW')
        ? 'This Ride Now promotion needs an admin review before its new discount rules are activated.'
        : code.includes('PROMO_NOT_VALID_FOR_RIDE_NOW')
        ? 'That code is not available for Ride Now. Try a Ride Now offer.'
        : code.includes('PROMO_MINIMUM_NOT_MET')
          ? 'This ride does not meet the minimum fare for that promo.'
          : code.includes('PROMO_USAGE_LIMIT_REACHED')||code.includes('PROMO_CAMPAIGN_LIMIT_REACHED')
            ? 'This promo has reached its redemption limit.'
            : code.includes('PROMO_LOCKED_BY_PAYMENT')
              ? 'Your card authorization has already started, so the fare is locked.'
              : code.includes('PROMO_NOT_VALID_FOR_MARKET')
                ? 'This promo is not available in your UniAir market.'
                : 'That promo could not be applied. Check the code and try again.';
      setPromoError(description);
    }finally{setPromoBusy(false);}
  }

  async function run(action) {
    if (busy || !requestId || ["expired", "confirmed", "no_driver", "released"].includes(stage) ||
        (stage === "checking" && !(action === "verify" && autoVerifyOnReturn))) return;
    if (action === "prepare" && (stage === "supply_search" || stage === "supply_timeout")) {
      // Only clear the no-supply latch after payment or funding really starts.
      setStage('checking');
    }
    setBusy(true);
    setMessage("");
    try {
      if (action === "prepare") {
        reportMilestone('payment_method_selected',
          companyFunded ? 'SPONSORED' : paymentMethod === 'wallet'
            ? 'WALLET' : selectedCard ? 'SAVED_CARD' : 'NEW_CARD');
        reportMilestone('payment_started');
        // Revalidate immediately before Paystack: a tab restored from
        // sessionStorage may refer to a request that expired minutes ago.
        const { data: latest, error: statusError } = await supabase.rpc(
          "uniair_rapid_my_request_status_v1", { p_request_id: requestId },
        );
        if (statusError || !latest?.ok) {
          setStage("status_error");
          throw new Error("Could not verify this request. No Paystack authorization was started.");
        }
        if (latest.payment_setup_unavailable === true) {
          setStage("payment_setup_unavailable");
          setCheckoutUrl(null);
          setMessage("Paystack checkout could not be opened. UniAir must verify the earlier attempt before another payment.");
          return;
        }
        const unavailable = latest.request_status !== "requested" ||
          (latest.request_expires_at &&
            Date.parse(latest.request_expires_at) <= Date.now());
        if (unavailable) {
          if (["created", "not_started", "failed", "expired"].includes(latest.hold_status)) {
            setStage("expired");
            setMessage("Your earlier Ride Now request has expired or closed. No card hold was authorized. Start a fresh request to continue.");
          } else {
            setStage("needs_verification");
            setMessage("The request is closed, but an earlier authorization needs verification. Do not start another payment yet.");
          }
          return;
        }
        // R0 is company-funded. Positive amounts below R5 cannot go to Paystack.
        // Fail before card consent, hold creation, or external redirects.
        if (paymentMethod!=='wallet' && Number(latest.final_fare)>0 && Number(latest.final_fare)<5) {
          setStage('idle');
          setCheckoutUrl(null);
          setPromoError('This promo leaves a fare below Paystack’s R5 minimum. Remove it or choose another offer; no card hold was started.');
          return;
        }
        if (Number(latest.final_fare)===0) {
          // A fully funded booking is not a Paystack reservation.
          const {data:funded,error:fundError}=await supabase.functions.invoke(
            'uniair-rapid-company-promo',{
              body:{action:'reserve',request_id:requestId},
            });
          if(fundError||!funded?.ok){
            const result=fundError?.context?.json
              ?await fundError.context.json().catch(()=>null):null;
            if((result?.code||funded?.code)==='NO_LIKELY_DRIVER_SUPPLY'){
              markNoSupply();
              setStage('supply_search');
              setCheckoutUrl(null);
              throw new Error('Sorry, no student drivers are available right now. Your promo was not used; no card was charged.');
            }
            setStage('needs_verification');
            throw new Error('UniAir could not verify funding for this free ride. No Paystack charge was started.');
          }
          clearNoSupply();
          setCompanyFunded(true);
          setStage('authorized');
          setCheckoutUrl(null);
          setSearchExpiresAt(funded.search_expires_at||null);
          setMessage('UniAir is covering your entire fare. Finding your student driver.');
          void checkTrip();
          return;
        }
        if (paymentMethod==='wallet' && Number(latest.final_fare)>0) {
          if(Number(latest.wallet_balance||0)<Number(latest.final_fare||0) &&
             latest.wallet_status!=='reserved') {
            setStage('idle');
            throw new Error('Your UniAir Wallet balance is below the fare. Top up your wallet or choose a card.');
          }
          const {data:walletResult,error:walletError}=await supabase.functions.invoke(
            'uniair-rapid-wallet',{body:{action:'reserve',request_id:requestId}});
          if(walletError || !walletResult?.ok) {
            const detail=walletError?.context?.json
              ?await walletError.context.json().catch(()=>null):null;
            const code=detail?.code||walletResult?.code;
            if(code==='NO_LIKELY_DRIVER_SUPPLY'){
              markNoSupply();setStage('supply_search');
              throw new Error('No eligible student drivers are nearby. Your wallet was not debited.');
            }
            if(code==='INSUFFICIENT_WALLET_BALANCE'){
              setStage('idle');
              throw new Error('Insufficient UniAir Wallet balance. Top up or use your card.');
            }
            if(code==='WALLET_RESERVATION_TERMINAL'){
              setStage('needs_verification');
              throw new Error('This wallet reservation is closed. Start a new Ride Now request.');
            }
            setStage('wallet_search_pending');
            throw new Error('Unable to verify your wallet reservation. Check again; no second deduction will be started.');
          }
          setWalletUsed(true);
          setWalletStatus('reserved');
          if(walletResult.wallet_balance!==undefined &&
             Number.isFinite(Number(walletResult.wallet_balance)))
            setWalletBalance(Number(walletResult.wallet_balance));
          if(walletResult.status==='confirmed') {
            await checkTrip();return;
          }
          clearNoSupply();setCheckoutUrl(null);
          setStage(walletResult.status==='search_pending'?'wallet_search_pending':'authorized');
          setSearchExpiresAt(walletResult.search_expires_at||null);
          setMessage(walletResult.status==='search_pending'
            ?'Your wallet fare is reserved. Retry driver search without paying again.'
            :'Fare reserved in UniAir Wallet. Finding your student driver.');
          void checkTrip();
          return;
        }
        if(['created','not_started'].includes(latest.hold_status)){
          const {error:consentError}=await supabase.rpc(
            'uniair_rapid_set_my_card_save_consent_v1',{
              p_request_id:requestId,
              p_save:!selectedCard && saveNewCard,
            });
          if(consentError)throw new Error(
            'Could not securely record your card-saving choice. No authorization was started.');
        }
        // pending_customer can only resume the *existing* provider checkout.
        if (!["created", "not_started", "pending_customer"].includes(latest.hold_status)) {
          setStage("needs_verification");
          setMessage("An authorization is already in progress for this request. Verify its status rather than starting another.");
          return;
        }
      }
      const { data, error } = await supabase.functions.invoke("uniair-rapid-preauthorization", {
        body: { action, request_id: requestId,
          ...(action === "prepare" && selectedCard ? { saved_card_id: selectedCard } : {}),
        },
      });
      if (error) {
        const response = error?.context && typeof error.context.json === "function"
          ? await error.context.json().catch(() => null) : null;
        if (response?.code === "PROVIDER_VERIFICATION_UNAVAILABLE") {
          setStage("verification_unavailable");
          setCheckoutUrl(null);
          throw new Error("Paystack verification is currently unavailable. Your ride is not confirmed. UniAir is checking the existing authorization; please do not pay again.");
        }
        if (response?.code === "PROMO_BELOW_PAYSTACK_MINIMUM") {
          setStage("idle");
          setCheckoutUrl(null);
          setPromoError("Paystack requires a fare of at least R5, unless UniAir fully sponsors the ride at R0. No card hold was created.");
          return;
        }
        if (response?.code === "PAYMENT_SETUP_UNAVAILABLE") {
          setStage("payment_setup_unavailable");
          setCheckoutUrl(null);
          throw new Error("Paystack checkout could not be opened. Your ride is not confirmed, and UniAir must review the authorization outcome. Please contact support rather than retrying payment.");
        }
        if (["PROVIDER_OUTCOME_UNCONFIRMED", "PROVIDER_VERIFICATION_PENDING",
          "PREAUTH_ALREADY_IN_PROGRESS"].includes(response?.code)) {
          setStage("needs_verification");
          throw new Error("Paystack has not confirmed this authorization yet. No new card hold will be started. Please check again later or contact UniAir support.");
        }
        if (response?.code === "RAPID_SINGLE_ASAP_REQUEST_REQUIRED" ||
            response?.code === "REQUEST_EXPIRED" ||
            /RAPID_SINGLE_ASAP_REQUEST_REQUIRED|REQUEST_EXPIRED/.test(response?.message || "")) {
          await checkTrip();
          throw new Error("This request is no longer eligible for Ride Now authorization. Return to a fresh request instead of retrying payment.");
        }
        if (response?.code === "NO_LIKELY_DRIVER_SUPPLY") {
          markNoSupply();
          setStage("supply_search");
          setCheckoutUrl(null);
          throw new Error("No eligible Ride Now driver has fresh GPS in your pickup area. No Paystack authorization was started. Ask the driver to refresh live GPS and try again before this request expires.");
        }
        throw new Error(response?.message || response?.code || error.message);
      }
      if (!data?.ok) {
        if (data?.code === "PROVIDER_VERIFICATION_UNAVAILABLE") {
          setStage("verification_unavailable");
          setCheckoutUrl(null);
          throw new Error("Paystack verification is currently unavailable. Your ride is not confirmed. UniAir is checking the existing authorization; please do not pay again.");
        }
        if (data?.code === "PROMO_BELOW_PAYSTACK_MINIMUM") {
          setStage("idle");
          setCheckoutUrl(null);
          setPromoError("Paystack requires a fare of at least R5, unless UniAir fully sponsors the ride at R0. No card hold was created.");
          return;
        }
        if (data?.code === "PAYMENT_SETUP_UNAVAILABLE") {
          setStage("payment_setup_unavailable");
          setCheckoutUrl(null);
          throw new Error("Paystack checkout could not be opened. Your ride is not confirmed, and UniAir must review the authorization outcome. Please contact support rather than retrying payment.");
        }
        if (["PROVIDER_OUTCOME_UNCONFIRMED", "PROVIDER_VERIFICATION_PENDING",
          "PREAUTH_ALREADY_IN_PROGRESS"].includes(data?.code)) {
          setStage("needs_verification");
          throw new Error("Paystack has not confirmed this authorization yet. No new card hold will be started. Please check again later or contact UniAir support.");
        }
        if (data?.code === "RAPID_SINGLE_ASAP_REQUEST_REQUIRED" ||
            data?.code === "REQUEST_EXPIRED" ||
            /RAPID_SINGLE_ASAP_REQUEST_REQUIRED|REQUEST_EXPIRED/.test(data?.message || "")) {
          await checkTrip();
          throw new Error("This request is no longer eligible for Ride Now authorization. Return to a fresh request instead of retrying payment.");
        }
        if (data?.code === "NO_LIKELY_DRIVER_SUPPLY") {
          markNoSupply();
          setStage("supply_search");
          setCheckoutUrl(null);
          throw new Error("No eligible Ride Now driver has fresh GPS in your pickup area. No Paystack authorization was started. Ask the driver to refresh live GPS and try again before this request expires.");
        }
        throw new Error(data?.message || data?.code ||
          "Card authorization could not be verified. Your ride is not confirmed.");
      }
      if (data.status === "pending_customer") {
        clearNoSupply();
        reportMilestone('payment_started');
        const hosted = safeCheckoutUrl(data.authorization_url);
        if (!hosted) throw new Error("UniAir did not receive a verified Paystack checkout link.");
        setCheckoutUrl(hosted);
        setStage("pending_customer");
        setMessage(action === "prepare"
          ? "Continue with Paystack. You'll return to UniAir after authorizing."
          : "Waiting for Paystack to confirm your earlier authorization.");
        // Verifying a returning checkout must never reopen Paystack.
        if (action === "prepare") window.location.assign(hosted);
      } else if (data.status === "not_started") {
        await checkTrip();
      } else if (data.status === "authorized") {
        clearNoSupply();
        reportMilestone('payment_authorized');
        setStage("authorized");
        setCheckoutUrl(null);
        setMessage(companyFunded
          ? "UniAir is covering this ride. Looking for your driver."
          : "Card authorized. UniAir is finding an available driver. No money has been captured.");
        void checkTrip();
      } else {
        setMessage("Paystack status: " + String(data.status || "pending") +
          ". Check the payment status again before retrying.");
      }
    } catch (error) {
      setMessage(error?.message || "Could not verify your card authorization.");
    } finally {
      setBusy(false);
    }
  }

  // Check newly online drivers quickly while keeping payment authorization
  // guarded. Single-flight polling prevents overlapping requests.
  useEffect(() => {
    if (stage !== "supply_search" || !requestId) return undefined;
    let active = true;
    const tryConnect = () => {
      if (supplyPreparingRef.current ||
          Date.now() - supplyAutoResumeAt.current < 10000) return;
      supplyAutoResumeAt.current = Date.now();
      supplyPreparingRef.current = true;
      setConnectingToDriver(true);
      void run("prepare").finally(() => {
        supplyPreparingRef.current = false;
        setConnectingToDriver(false);
      });
    };
    const pollDemand = async () => {
      if (supplyPollingInFlightRef.current || !active) return;
      supplyPollingInFlightRef.current = true;
      try {
        if (demandRegistered.current !== requestId) {
          const { data, error } = await supabase.rpc("uniair_rapid_register_demand_v1", {
            p_request_id: requestId,
          });
          if (!active) return;
          if (data?.code === "DRIVERS_AVAILABLE_TRY_AGAIN") {
            tryConnect();
            return;
          }
          if (error || !data?.ok) {
            if (["DEMAND_ALERTS_DISABLED","DEMAND_ALERT_RATE_LIMIT","REQUEST_NOT_ELIGIBLE"]
              .includes(data?.code)) {
              setStage("status_error");
              setMessage("This search is unavailable. Return to your dashboard to start another ride.");
            }
            return;
          }
          demandRegistered.current = requestId;
        }
        const { data, error } = await supabase.rpc("uniair_rapid_my_demand_status_v1", {
          p_request_id: requestId,
        });
        if (!active || error || !data?.ok) return;
        if (data.expires_at) {
          supplyDeadlineRef.current = data.expires_at;
          setSearchExpiresAt(data.expires_at);
        }
        const remaining = Date.parse(data.expires_at || supplyDeadlineRef.current || "") - Date.now();
        if (remaining <= 0 || (data.registered && !data.active)) {
          setSafeToSchedule(true);
          setStage("supply_timeout");
          reportMilestone("driver_search_expired", "NO_DRIVER_AVAILABLE");
          return;
        }
        if (data.driver_available && remaining > 50000) tryConnect();
      } finally {
        supplyPollingInFlightRef.current = false;
      }
    };
    void pollDemand();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void pollDemand();
    }, 2500);
    const onVisible = () => {
      if (document.visibilityState === "visible") void pollDemand();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, requestId]);

  // Restore the Paystack return automatically using ONLY verification.
  // Never initialize a second hold from a browser redirect.
  useEffect(() => {
    if (!autoVerifyOnReturn || !requestId || returnVerificationStarted.current) return;
    returnVerificationStarted.current = true;
    void run("verify");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVerifyOnReturn, requestId]);

  // A confirmed Ride Now is a trip, not another step in a booking wizard.
  // The button remains a fallback, but normal flow opens the trip automatically.
  useEffect(() => {
    if (stage !== "confirmed" || !confirmedTripId) return undefined;
    const timeout = window.setTimeout(() => onFinished?.(confirmedTripId), 800);
    return () => window.clearTimeout(timeout);
  }, [stage, confirmedTripId, onFinished]);

  // Keep the completed five-minute search on screen. Passengers choose to
  // schedule instead or exit; never auto-redirect away from these options.

  // Pending webhooks and Paystack verification may arrive a few seconds after
  // return. Retry read-only verification a few times without reopening checkout.
  const autoVerifyAttempts = useRef(0);
  useEffect(() => {
    if (!autoVerifyOnReturn || busy ||
        !["pending_customer","needs_verification"].includes(stage) ||
        autoVerifyAttempts.current >= 4) return undefined;
    const timer = window.setTimeout(() => {
      autoVerifyAttempts.current += 1;
      void run("verify");
    }, 7000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVerifyOnReturn, stage, busy]);

  // No second confirm button for new Ride Now requests with no saved cards.
  // A restored/Paystack-returned session never initiates a new hold.
  useEffect(() => {
    if (!autoBegin || autoVerifyOnReturn || !savedCardsLoaded ||
        savedCards.length > 0 || stage !== "idle" ||
        autoPrepareInitiated.current) return;
    autoPrepareInitiated.current = true;
    void run("prepare");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBegin, autoVerifyOnReturn, savedCardsLoaded, savedCards.length, stage]);

  async function closeUnpaidSearch(schedule = false) {
    if (busy || !requestId) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("uniair_rapid_cancel_my_unpaid_demand_v1", {
        p_request_id: requestId,
      });
      if (error || !data?.ok) {
        await checkTrip();
        throw new Error("Your request has changed. Please check its latest status.");
      }
      clearNoSupply();
      reportMilestone("driver_search_cancelled", "SEARCH_CANCELLED");
      if (schedule) onScheduleInstead?.();
      else onLeave?.();
    } catch (error) {
      setMessage(error?.message || "Could not cancel this search.");
      setStage("status_error");
    } finally { setBusy(false); }
  }

  async function exitFinishedSearch() {
    if (busy) return;
    if (stage === "supply_timeout" || (stage === "supply_search" && timedOutOnScreen)) {
      await closeUnpaidSearch();
      return;
    }
    if (requestId && searchExpiresAt && Date.parse(searchExpiresAt) <= Date.now() &&
        !confirmed && stage !== "released") {
      setBusy(true);
      try {
        const { data, error } = await supabase.rpc("uniair_rapid_finish_my_expired_search_v1", {
          p_request_id: requestId,
        });
        if (error || !data?.ok) {
          await checkTrip();
          setMessage("Checking the latest status before closing.");
          return;
        }
        onLeave?.();
      } finally { setBusy(false); }
      return;
    }
    onLeave?.();
  }

  async function cancelSearch() {
    if (stage === "supply_search") {
      await closeUnpaidSearch();
      return;
    }
    if (busy || !requestId || stage !== "authorized") return;
    setBusy(true);
    setMessage("");
    try {
      const { data, error } = await supabase.rpc('uniair_cancel_my_rapid_search_v1', {
        p_request_id: requestId,
      });
      if (error || !data?.ok) {
        // A driver may have confirmed while the passenger was cancelling.
        // Never pretend cancellation succeeded if the server rejected it.
        await checkTrip();
        throw new Error(data?.code === 'DRIVER_ALREADY_CONFIRMED_OR_SEARCH_CLOSED'
          ? "Your driver may already be confirmed. Checking the latest ride status."
          : error?.message || 'Could not cancel the search. Please check its status.');
      }
      reportMilestone('driver_search_cancelled', 'SEARCH_CANCELLED');
      setStage('no_driver');
      setCheckoutUrl(null);
      onLeave?.();
      setMessage(walletUsed
        ? 'Search cancelled. The reserved fare has been returned to UniAir Wallet.'
        : 'Search cancelled. No fee. The unused card hold will be released after Paystack confirmation.');
    } catch (error) {
      setMessage(error?.message || 'Unable to cancel the driver search.');
    } finally {
      setBusy(false);
    }
  }

  // The server verifies expiry and atomically serializes with driver
  // confirmation before revoking offers and initiating any payment release.
  useEffect(() => {
    if (!requestId || !searchExpiresAt ||
        !["authorized", "search_finalizing"].includes(stage) ||
        Date.parse(searchExpiresAt) > now || expirationInFlight.current ||
        now - lastExpirationAttemptAt.current < 3000) return;
    lastExpirationAttemptAt.current = now;
    expirationInFlight.current = true;
    setStage("search_finalizing");
    void supabase.rpc("uniair_rapid_finish_my_expired_search_v1", {
      p_request_id: requestId,
    }).then(() => checkTrip())
      .finally(() => { expirationInFlight.current = false; });
  }, [stage, requestId, searchExpiresAt, now, checkTrip]);

  const confirmed = stage === "confirmed";
  const formattedZar=(value)=>`R ${Number(value||0).toFixed(2)}`;
  const expired = stage === "expired";
  const noDriver = stage === "no_driver" || stage === "released";
  const secondsRemaining = searchExpiresAt
    ? Math.max(0, Math.ceil((Date.parse(searchExpiresAt) - now) / 1000))
    : null;
  const searchCountdown = secondsRemaining === null ? null
    : `${Math.floor(secondsRemaining / 60)}:${String(secondsRemaining % 60).padStart(2, "0")}`;
  const searchAgeSeconds = secondsRemaining !== null
    ? Math.max(0,300-secondsRemaining)
    : searchStartedAt ? Math.max(0,Math.round((now-Date.parse(searchStartedAt))/1000)) : 0;
  const searchProgressMessage = rideNowSearchProgress(searchAgeSeconds);
  const searchActivityMessage = rideNowActivityMessage({
    reserved:searchReserved,connecting:connectingToDriver,
    hasOffers:activeDriverOffers>0,recentDecline:recentDeclineUntil>now,
  });
  const hasDriverOffers=driversContacted>0;
  const scheduleAlternativeAvailable=safeToSchedule || stage==='supply_timeout';
  const timedOutOnScreen = !!searchExpiresAt && secondsRemaining === 0 &&
    (['authorized','supply_search','search_finalizing'].includes(stage) ||
      (stage === 'checking' && connectingToDriver));
  const searchFinished = timedOutOnScreen ||
    ['search_finalizing','no_driver','released','supply_timeout'].includes(stage) ||
    (stage === 'expired' && !!searchExpiresAt);
  const releaseInProgress = stage === 'no_driver' && !safeToSchedule;
  const releaseComplete = stage === 'released' || (stage === 'no_driver' && safeToSchedule);
  const noCardHoldEver = companyFunded || walletUsed ||
    ['created','not_started','failed','expired'].includes(paymentHoldStatus);
  const releaseDoneLabel = noCardHoldEver ? 'No payment was taken' : 'Authorization released';

  if (!searchFinished && (stage === "authorized" || stage === "supply_search" ||
       (connectingToDriver && stage === "checking")) && !confirmed) {
    return (
      <section className="rounded-3xl border border-[#720e1e]/15 bg-white px-5 py-10 text-center shadow-sm"
        aria-label="Finding your Ride Now driver" aria-live="polite">
        <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
          <span aria-hidden="true" className="absolute h-24 w-24 rounded-full bg-[#720e1e]/10 motion-safe:animate-pulse" />
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#720e1e] text-white">
            {searchReserved ? <CheckCircle2 size={30} /> : <CarFront size={30} />}
          </span>
        </div>
        <h2 className="text-xl font-extrabold text-gray-950">
          Finding your driver
        </h2>
        <p className="mt-2 text-sm text-gray-500" aria-live="polite">{searchProgressMessage}</p>
        {searchActivityMessage && (
          <p className="mt-3 rounded-xl bg-[#720e1e]/[0.05] px-3 py-2 text-sm font-medium text-[#720e1e]"
            aria-live="polite">{searchActivityMessage}</p>
        )}
        <p className="mt-5 text-sm font-semibold text-[#720e1e]">
          {searchCountdown ? `Time remaining · ${searchCountdown}` : "Searching…"}
        </p>
        
        <button type="button" disabled={busy} onClick={() => void cancelSearch()}
          className="mt-6 block w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-800 disabled:opacity-50">
          {busy ? "Cancelling…" : "Cancel search"}
        </button>

      </section>
    );
  }
  // Payment methods remain protected by their existing authorization gates.
  const allowNewPrepare = stage === "idle" || stage === "pending_customer";
  return (
    <section className="rounded-3xl border border-[#720e1e]/15 bg-white p-5 shadow-sm"
      aria-label="Ride Now secure payment">
      <p className="text-xs font-bold uppercase tracking-wide text-[#720e1e]">UniAir Ride Now</p>
      <h2 className="mt-2 text-xl font-bold text-gray-950">
        {confirmed ? "Driver confirmed" : expired ? "Request expired" :
          searchFinished ? "No driver found" :
          stage === "checking" ? "Checking your Ride Now request" :
          stage === "status_error" ? "We couldn't verify this request" :
          releaseComplete ? releaseDoneLabel :
           releaseInProgress ? "Releasing your card authorization" :
           noDriver ? "No driver found" :
          stage === "pending_customer" ? (autoVerifyOnReturn ? "Confirming your payment" : "Secure card checkout") :
          stage === "payment_setup_unavailable" ? "Payment setup unavailable" :
          searchFinished ? "No driver found" :
          stage === "verification_unavailable" ? "Waiting for Paystack verification" :
          stage === "needs_verification" ? "Checking your earlier payment" :
           stage === "wallet_search_pending" ? "Ready to find your driver" :
          stage === "authorized" ? "Finding your driver" :
          "Choose how to pay before finding your driver"}
      </h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        {confirmed
          ? "Your booking is confirmed. Follow the trip from your dashboard."
          : searchFinished
            ? "We couldn't confirm a driver within five minutes."
          : stage === "verification_unavailable"
            ? "Paystack checkout was opened, but UniAir cannot verify the card hold yet. Do not authorize another payment."
            : stage === "payment_setup_unavailable"
            ? "We could not open Paystack's secure card checkout. This ride is not booked, and payment status must be checked before another attempt."
            : releaseInProgress
              ? (walletUsed ? "Checking your wallet refund." : "UniAir is waiting for Paystack to confirm the unused authorization is released.")
              : releaseComplete
                ? (walletUsed ? "Your unused wallet fare has been returned." : noCardHoldEver ? "No payment was taken." : "Your card authorization has been released.")
              : searchFinished
              ? "We couldn't confirm a driver within five minutes."
              : (walletUsed||paymentMethod==='wallet' ? "Wallet reserves your fare before searching, returns it if no driver is found, and finalises payment after the ride." : "Your card is authorized, not charged. UniAir confirms driver availability before starting the trip. Cancellation fees are currently disabled; payment is only captured after a completed ride.")}
      </p>
      {!confirmed && stage === 'idle' && fare && (
        <div className="mt-5 space-y-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs font-black uppercase tracking-wide text-[#720e1e]">Your Ride Now</p>
          <p className="text-sm font-semibold text-gray-900">{fare.pickup}</p>
          <p className="text-sm font-semibold text-gray-900">↓ {fare.destination}</p>
          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-500">{fare.passengers} passenger{fare.passengers===1?'':'s'}</p>
            <p className="text-xl font-extrabold text-gray-950">{formattedZar(fare.total)}</p>
          </div>
          {fare.total===0&&<p className="rounded-xl bg-green-50 p-3 text-xs font-bold text-green-800">Fully sponsored by UniAir — you pay R0. Your driver still receives the agreed payout.</p>}
          {paymentMethod!=='wallet'&&fare.total>0&&fare.total<5&&<p role="alert" className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-900">This discounted fare is below Paystack’s R5 card minimum. Remove the promo or use a different one; your card will not be authorized.</p>}
          {fare.discount>0&&<div className="flex justify-between text-xs">
            <span className="text-gray-500">Original fare {formattedZar(fare.original)}</span>
            <span className="font-bold text-green-700">− {formattedZar(fare.discount)}</span>
          </div>}
        </div>
      )}
      {!confirmed && stage === 'idle' && (
        <section className="mt-4 rounded-2xl border border-gray-200 p-4">
          <p className="text-sm font-extrabold text-gray-900">Promo code</p>
          {fare?.code ? (
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-green-700">{fare.code} applied</p>
              <button type="button" disabled={promoBusy||busy}
                onClick={()=>void applyPromo('')} className="text-sm font-bold text-[#720e1e]">
                Remove
              </button>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <input type="text" autoComplete="off" value={promoInput}
                maxLength={32} placeholder="Enter code"
                onChange={event=>{setPromoInput(event.target.value.toUpperCase());setPromoError('');}}
                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm uppercase"
                aria-label="Ride Now promo code"/>
              <button type="button" disabled={promoBusy||busy||!promoInput.trim()}
                onClick={()=>void applyPromo()} className="rounded-xl border border-[#720e1e] px-4 py-3 text-sm font-bold text-[#720e1e] disabled:opacity-40">
                {promoBusy?'Applying…':'Apply'}
              </button>
            </div>
          )}
          {promoError&&<p role="alert" className="mt-2 text-xs text-red-700">{promoError}</p>}
          {promoNote&&<p role="status" className="mt-2 text-xs text-green-700">{promoNote}</p>}
        </section>
      )}
      {!confirmed && stage==='idle' && Number(fare?.total)>0 && (
        <fieldset className="mt-4 space-y-2 rounded-2xl border border-gray-200 p-4">
          <legend className="px-1 text-sm font-extrabold text-gray-900">Payment method</legend>
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 p-3 text-sm">
              <input type="radio" name="rapidPaymentCard"
                checked={paymentMethod==='wallet'}
                onChange={()=>setPaymentMethod('wallet')}/>
              <Wallet className="h-5 w-5 text-[#720e1e]" aria-hidden="true"/>
              <span className="min-w-0 flex-1 font-semibold">UniAir Wallet
                <span className="block text-xs font-normal text-gray-500">
                  {walletBalance===null
                    ? 'Balance temporarily unavailable'
                    : `Available ${formattedZar(walletBalance)}${walletBalance<Number(fare?.total||0)?' · Insufficient balance':''}`}
                </span>
              </span>
          </label>
          {paymentMethod==='card'&&!savedCardsLoaded?<p className="text-xs text-gray-600">Checking saved cards…</p>:null}
          {savedCards.map(card=>(
            <label key={card.saved_card_id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 text-sm">
              <input type="radio" name="rapidPaymentCard"
                checked={paymentMethod==='card'&&selectedCard===card.saved_card_id}
                onChange={()=>{setPaymentMethod('card');setSelectedCard(card.saved_card_id);}}/>
              <span className="flex-1">{card.card_type||'Card'} •••• {card.last4||'••••'}{card.bank?` · ${card.bank}`:''}</span>
              <span className="text-xs font-semibold text-[#720e1e]">Saved</span>
            </label>
          ))}
          <label className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 text-sm">
            <input type="radio" name="rapidPaymentCard" checked={paymentMethod==='card'&&!selectedCard}
              onChange={()=>{setPaymentMethod('card');setSelectedCard('');}}/>
            {savedCards.length?'Use another card':'Add a card with Paystack'}
          </label>
          {paymentMethod==='card'&&!selectedCard&&<label className="flex items-start gap-3 rounded-xl bg-gray-50 p-3 text-xs leading-5 text-gray-700">
            <input type="checkbox" checked={saveNewCard}
              onChange={event=>setSaveNewCard(event.target.checked)}
              className="mt-1"/>
            <span>Securely save this card for future UniAir rides after this trip is paid.
              Your card details stay with Paystack; UniAir stores only an encrypted reusable authorization.</span>
          </label>}
          <p className="text-xs text-gray-500">{paymentMethod==='wallet' ? 'Your fare is reserved from Wallet when you confirm, returned if no driver is found, and finalised after trip completion.' : 'Your card is authorized now and charged only after a completed ride.'}</p>
        </fieldset>
      )}
      {!confirmed && stage === "checking" && (
        <p role="status" className="mt-4 text-sm text-gray-600">Checking your ride and available payment methods…</p>
      )}
      {!confirmed && stage === "status_error" && (
        <button type="button" disabled={busy} onClick={() => void checkTrip()}
          className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-bold">
          Retry request status
        </button>
      )}
      {!confirmed && expired && (
        <button type="button" onClick={() => onRetry?.()}
          className="mt-4 w-full rounded-xl bg-[#720e1e] px-4 py-3 font-bold text-white">
          Start a new Ride Now request
        </button>
      )}
      {autoBegin && stage === "idle" && !savedCardsLoaded && (
        <p role="status" className="mt-4 text-sm text-gray-600">Preparing your secure card checkout…</p>
      )}
      {!confirmed && allowNewPrepare && !(autoBegin && stage === "idle" &&
          (!savedCardsLoaded || (savedCards.length === 0 && !message))) && (
        <button type="button" disabled={busy||promoBusy||(paymentMethod==='card'&&!savedCardsLoaded)||(stage==='idle'&&paymentMethod!=='wallet'&&Number(fare?.total)>0&&Number(fare?.total)<5)||(stage==='idle'&&paymentMethod==='wallet'&&Number(walletBalance||0)<Number(fare?.total||0))} onClick={() => run("prepare")}
          className="mt-4 w-full rounded-xl bg-[#720e1e] px-4 py-3 font-bold text-white disabled:opacity-50">
          {busy ? "Checking…" :
            stage === "pending_customer" ? "Resume existing secure checkout" :
            Number(fare?.total)===0 ? "Confirm free ride & find driver" :
            paymentMethod==='wallet' ? "Pay with Wallet & find driver" : selectedCard ? "Confirm & find my driver" : "Continue securely with Paystack"}
        </button>
      )}
      {stage==='wallet_search_pending'&&(
        <button type="button" disabled={busy}
          onClick={()=>{setPaymentMethod('wallet');void run('prepare');}}
          className="mt-3 w-full rounded-xl bg-[#720e1e] px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
          {busy?'Checking…':'Retry driver search · no extra wallet deduction'}
        </button>
      )}
      {checkoutUrl && !confirmed && stage === "pending_customer" && !autoVerifyOnReturn && (
        <a href={checkoutUrl}
          className="mt-3 block w-full rounded-xl border-2 border-[#720e1e] px-4 py-3 text-center text-sm font-bold text-[#720e1e]">
          Open secure Paystack checkout
        </a>
      )}
      {["needs_verification", "verification_unavailable"].includes(stage) && !confirmed && !noDriver && (
        <button type="button" disabled={busy} onClick={() => run("verify")}
          className="mt-3 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm font-bold text-gray-900 disabled:opacity-50">
          {busy ? "Checking…" : "Retry payment verification"}
        </button>
      )}
      {stage === "payment_setup_unavailable" && (
        <a href="mailto:support@uniair.co.za?subject=Ride%20Now%20payment%20setup%20unavailable"
          className="mt-3 block w-full rounded-xl border border-gray-300 px-4 py-3 text-center text-sm font-bold">
          Contact UniAir support
        </a>
      )}
      {stage === "authorized" && !confirmed && (
        <p className="mt-2 text-xs text-slate-600">
          {companyFunded ? 'UniAir funds your fare; no card hold is needed. ' :
            'Your card is authorized, not charged. '}
          Looking for a driver. Offers expire quickly; your booking is not confirmed until one accepts.
        </p>
      )}
      {(releaseInProgress || releaseComplete) && (
         <div role="status" aria-live="polite" className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
           <div className="flex items-center gap-3">
             {releaseComplete ? <CheckCircle2 className="text-green-700" size={27} /> :
               <span aria-hidden="true" className="h-7 w-7 shrink-0 rounded-full border-[3px] border-[#720e1e]/20 border-t-[#720e1e] motion-safe:animate-spin" />}
             <div>
               <p className="text-sm font-bold text-gray-900">{releaseComplete ? releaseDoneLabel : "Checking card authorization release"}</p>
               <p className="mt-1 text-xs text-gray-600">{releaseComplete ? "You can choose what to do next." : "UniAir is confirming the release."}</p>
             </div>
           </div>
           {!releaseComplete && <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-200"><div className="h-full w-1/2 rounded-full bg-[#720e1e] motion-safe:animate-pulse" /></div>}
           {!releaseComplete && <p className="mt-2 text-xs text-gray-500">This progress bar shows verification in progress, not a refund countdown.</p>}
         </div>
       )}
       {searchFinished && !confirmed && (
        <div className="mt-4 space-y-2 rounded-2xl border border-[#720e1e]/15 bg-[#720e1e]/[0.03] p-4">
          <h3 className="text-base font-extrabold text-gray-950">Would scheduling for later work?</h3>
          <p className="text-xs leading-5 text-gray-600">
            We'll keep your pickup, destination and passenger count so you don't have to start again.
          </p>
          <button type="button" onClick={()=>(stage==='supply_timeout' ||
              (stage==='supply_search' && timedOutOnScreen))
            ? void closeUnpaidSearch(true) : onScheduleInstead?.()}
            disabled={!scheduleAlternativeAvailable||busy}
            className="w-full rounded-xl bg-[#720e1e] px-4 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-50">
            Schedule this ride for later
          </button>
          {!scheduleAlternativeAvailable &&
            <p className="text-xs text-slate-600">Finishing the previous ride search before another booking.</p>}
        </div>
      )}
      {message && !searchFinished && <p role="status" className="mt-3 text-sm font-medium text-gray-700">{message}</p>}
      {confirmed && (
        <button type="button" onClick={() => onFinished?.(confirmedTripId)}
          className="mt-4 w-full rounded-xl bg-[#720e1e] px-4 py-3 font-bold text-white">
          View my trip
        </button>
      )}
      {!confirmed && (
        <button type="button" onClick={() => void exitFinishedSearch()}
          disabled={busy}
          className="mt-3 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-700 disabled:opacity-50">
          {searchFinished ? 'Cancel search' : 'Back to dashboard'}
        </button>
      )}
    </section>
  );
}
